import type { DynamoDbPolicy, DynamoDbOffer } from '@ourbranch/schema-types/dynamodb';

import type { OnboardingTaskName, InternalTaskName } from './constants';

// ========== CONFIG TYPES ==========

/**
 * Which team reviews completed task submissions in the staff tools.
 * - `'processing'` — General document review (e.g., proof-of uploads, stress-free switch)
 * - `'uw-ops'` — Underwriting operations (e.g., Fortified Home, Complete Inventory)
 */
export type ReviewTeam = 'processing' | 'uw-ops';

/**
 * A collapsible section of instructional content rendered inside a task card in account-fe.
 * Multiple sections are stacked vertically within the expanded task card view.
 *
 * Rendered by: `account-fe/src/tasks/components/task.tsx`
 */
export interface ContentSection {
  /** Section heading. Pass `null` to render a continuation section without a heading. */
  title: string | null;
  /** Secondary heading rendered below the title (e.g., "You can provide either of the following:") */
  subtitle?: string;
  /** Section body text. Pass a string for a paragraph, or a string[] for multiple items. */
  body: string | string[];
  /** When true and body is a string[], renders as a `<ul>` bulleted list instead of paragraphs. */
  isList?: boolean;
}

/**
 * Context object passed to `account.content()` at render time.
 * Built automatically by `getTaskConfig()` from the task record and config.
 */
export interface ContentContext {
  /** Formatted policy IDs joined with "and" (e.g., "P-123 and P-456") */
  affectedPolicies: string;
  /** Number of days the member has to complete the task (from `TaskConfig.daysAllowed`) */
  daysAllowed: number;
  /** Human-readable deadline (e.g., "Jan 15, 2025"), or empty string if no deadline */
  formattedDeadline: string;
  /** Raw task data from the task record — extract task-specific fields as needed */
  data: Record<string, any>;
  /** Short brand name (e.g., "Branch"). Defaults to "Branch" if not provided. */
  brandName: string;
  /** Full brand name (e.g., "Branch Insurance"). Defaults to "Branch Insurance" if not provided. */
  brandFullName: string;
}

/**
 * Content returned by `account.content()` for rendering a task card in account-fe.
 *
 * Rendered by: `account-fe/src/tasks/components/task.tsx`
 */
export interface TaskCardContent {
  /** Main heading displayed on the task card */
  title: string;
  /** Subtitle shown when the task is pending / not yet completed */
  incompleteSubtitle: string;
  /** Subtitle shown after the task has been successfully completed */
  completedSubtitle: string;
  /** Subtitle shown when staff has denied/rejected the submission (optional) */
  deniedSubtitle?: string;
  /** Label for the primary action button (e.g., "Upload proof", "Sign Documents") */
  actionLabel: string;
  /** Heading for the document upload modal. Defaults to "Upload documents" if omitted. */
  modalTitle?: string;
  /** Instructional content sections rendered inside the expanded task card */
  contentSections: ContentSection[];
}

/**
 * Account-fe rendering configuration for a task.
 * Controls how the task appears in the member-facing task center.
 *
 * Consumed by: `account-fe/src/tasks/`
 */
export interface AccountConfig {
  /**
   * Success toast notification shown after the member uploads a document.
   * Falls back to "Document uploaded successfully" if omitted.
   *
   * Consumed by: `account-fe/src/tasks/tasks.store.js` → `confirmUpload()`
   */
  toastMessage?: string;
  /**
   * Custom prefix text before the policy list on the task card (e.g., "Renewal of").
   * When set, the affected policies display reads like "Renewal of P-123 and P-456".
   *
   * Consumed by: `account-fe/src/tasks/components/affected-policies.tsx`
   */
  affectedPoliciesLeadText?: string;
  /**
   * Renders the task card content. Called by `getTaskConfig()` with a `ContentContext`
   * containing formatted policy IDs, deadline, days allowed, and raw task data.
   */
  content: (ctx: ContentContext) => TaskCardContent;
}

/** Context passed to a `staffDisplayName` function, allowing the name to be derived from task data. */
export interface StaffDisplayNameContext {
  data: Record<string, any>;
}

/**
 * Configuration for a single onboarding task.
 *
 * Defines how the task behaves across the system:
 * - Staff tools (display name, review routing)
 * - Member-facing account portal (card content, upload UX)
 * - Analytics (Segment event tracking)
 * - In-app guidance (Appcues modals)
 */
export interface TaskConfig {
  /**
   * Human-readable name shown in staff tools (staff-fe task center, Front tickets).
   * Not shown to members — use `account.content().title` for member-facing text.
   *
   * Can be a static string, or a function that receives task data to produce a
   * data-driven name (e.g., `({ data }) => \`Community Drive registration - ${data.customer}\``).
   * Resolve via `resolveStaffDisplayName()` from this package.
   */
  staffDisplayName: string | ((ctx: StaffDisplayNameContext) => string);
  /**
   * Appcues flow ID that triggers an in-app guidance modal when the member
   * views this task in account-fe. Omit if no Appcues flow exists for this task.
   */
  appcuesModalId?: string;
  /**
   * Which staff team reviews submissions for this task.
   * Used to route Front tickets and filter tasks in staff tools.
   */
  reviewTeam: ReviewTeam;
  /**
   * Number of days the member has to complete the task from the policy effective date.
   * Can be a static number or a function of state for carriers with different requirements per state.
   * Passed into `account.content()` via `ContentContext.daysAllowed` for deadline messaging.
   */
  daysAllowed: number | ((state: string) => number);
  /**
   * Feature flag for task creation. When false, `identifyPolicyTasks()` will not
   * generate this task for new policies. All tasks are currently enabled.
   */
  enabled: boolean;
  /**
   * Whether task submissions are automatically approved without staff review.
   *
   * - `true` — every submission auto-approves (e.g. Community Drive Registration).
   * - `false` / omitted — never auto-approves; staff must explicitly approve.
   * - `(taskData) => boolean` — predicate evaluated against the submission's
   *   `taskData` (e.g. UDD auto-approves only when `removedReason` is one of the
   *   no-review-required codes; resolve via `resolveAutoApprove()`).
   */
  autoApprove?: boolean | ((taskData: Record<string, unknown> | undefined) => boolean);
  /**
   * Whether the task is completed by uploading a document. When false, the staff
   * task center hides the upload CTA and document modal — the task is expected to
   * complete via some other backend path (e.g., Community Drive Registration auto-completes
   * on driver activation in the mobile app). Defaults to true if omitted.
   */
  supportsFileUpload?: boolean;
  /**
   * Segment analytics event name fired when staff completes/approves this task.
   * Sent via `buildSegmentEventBatch()`. If omitted, only the generic
   * "Task Completed" event is sent (no task-specific event).
   *
   * Examples: "Signed Application", "Completed Inventory", "Provided Proof of Good Student"
   */
  eventName?: string;
  /** Account-fe rendering configuration (task card content, toast, and modal). */
  account: AccountConfig;
}

// ========== TASK DATA TYPES ==========

/** Shared file upload shape (added when documents are uploaded) */
export interface TaskFile {
  fileName: string;
  hash: string;
  type: string;
  documentUrl: string;
  signedUrl: string;
}

// Per-task data interfaces (corrected against actual creation code)

/** Base type for tasks that use the generic upload component */
export interface CustomerProofData {
  files?: TaskFile[];
}

export interface SignApplicationData {
  offerId: string;
  documentType: 'Policy';
  applicationUrl?: string; // added by tasks-handler at read time, not persisted at creation
}

export interface DriverProofData extends CustomerProofData {
  customer: string;
}

export interface NeedProofOfPriorCoverageData extends CustomerProofData {
  customer: string;
  documentType: 'Internal';
  priorIndividualBILimit?: number;
  priorOccurrenceBILimit?: number;
  currentAutoCarrier?: string;
  currentAutoCarrierPolicyNumber?: string;
  currentAutoCarrierStartDate?: string;
  hadInforceCVPolicy?: boolean;
  receivedCVData?: boolean;
}

export interface StressFreeSwitchData {
  documentType?: 'Internal';
  priorAutoInsuranceProvider?: string;
  priorHomeInsuranceProvider?: string;
  files?: TaskFile[];
}

export interface CompleteInventoryData {
  documentType: 'Internal';
  documentUrl?: string; // added by inventory-docs Lambda on completion
  isBundled?: boolean;
}

export interface ProvideProofOfConnectedHomeData extends CustomerProofData {
  connectedHomeProvider: string;
  documentType: 'Internal';
}

export interface ScheduledPersonalPropertyItem {
  category: string;
  description: string;
  value: number;
}

/** Image reference stored in an SPP snapshot — points to an S3 object, not the actual bytes. */
export interface SPPImageRef {
  hash: string;
  uri: string;
  s3UploadKey: string;
  type: string;
}

/** Per-view image slot in an SPP section snapshot */
export interface SPPViewSnapshot {
  image: SPPImageRef | null;
  comment?: string;
}

/** Single SPP item section as captured at submission time */
export interface SPPSectionSnapshot {
  propertyId: string;
  category: string;
  description: string;
  value: number;
  confirmationTimestampISO8601: string | null;
  aboveView: SPPViewSnapshot;
  sideView: SPPViewSnapshot;
  behindView: SPPViewSnapshot;
  headOnView: SPPViewSnapshot;
  appraisalView: SPPViewSnapshot;
}

/** Full SPP inventory snapshot stored on task.data when the member submits */
export interface SPPInventorySnapshot {
  sections: SPPSectionSnapshot[];
  images: Record<string, SPPImageRef>;
}

export interface NeedScheduledPersonalPropertyImagesData extends CustomerProofData {
  items: ScheduledPersonalPropertyItem[];
  documentType: 'Internal';
  /** Populated when the member submits their SPP inventory */
  sppSnapshot?: SPPInventorySnapshot;
}

export interface ConfirmWindstormMitigationStatusData extends CustomerProofData {
  windstormMitigationLevel: number | null;
  documentType: 'Internal';
}

export interface ConnectedHomeRenewalData extends CustomerProofData {}

export interface ReviewPotentialUndisclosedDriverData extends CustomerProofData {
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    age: number;
    gender?: string;
    licenseNumber?: string;
    licenseState?: string;
  };
  renewalDate?: string;
  encryptedLink?: string;
  attestationFormUrl?: string;
  /** Set when the member completes the task */
  removedReason?: string;
  /** Set when the member completes the task */
  additionalComments?: string;
}

export interface ProofOfMIPipOption4Data extends CustomerProofData {
  pipLimit: string;
  primaryDriver: string;
  documentType: 'Internal';
  residents: Array<{ name: string; hasQHC: boolean }>;
  excludedResidents: Array<{ name: string }>;
}

/** Shared data shape for MI PIP Options 5 (Medicaid/$50K) and 6 (Medicare/Opt-out) */
export interface ProofOfMIPipOption5Or6Data extends CustomerProofData {
  pipLimit: string;
  primaryDriver: string;
  documentType: 'Internal';
  residents: Array<{ name: string; hasQHC: boolean }>;
  otherResidents: Array<{ name: string }>;
}

export type ProofOfMIPipOption5Data = ProofOfMIPipOption5Or6Data;
export type ProofOfMIPipOption6Data = ProofOfMIPipOption5Or6Data;

export interface ProvideProofOfFortifiedHomeData extends CustomerProofData {
  documentType: 'Internal';
}

export interface VerifyRoofYearData extends CustomerProofData {
  documentType: 'Internal';
  roofYear: number;
  roofReplacementYearMax: number;
}

export interface CommunityDriveRegistrationData {
  customer: string;
  driverId: string;
}

/**
 * Pairs a task name constant with its config. Exported by each file in `config/tasks/`.
 * The registry auto-builds from all TaskDefinitions collected in `tasks/index.ts`.
 */
export interface TaskDefinition {
  /** Task name from `onboardingTasks` constant (e.g., `onboardingTasks.SignApplication`) */
  taskName: OnboardingTaskName;
  /** Full task configuration */
  config: TaskConfig;
}

/**
 * Fully rendered task card content returned by `getTaskConfig()`.
 * Extends `TaskCardContent` with account-level display properties.
 *
 * Consumed by: `account-fe/src/tasks/components/task.tsx`
 */
export interface RenderedTaskConfig extends TaskCardContent {
  toastMessage?: string;
  affectedPoliciesLeadText?: string;
}

/** Input to `getTaskConfig()` — typically built from a task record at render time. */
export interface TaskInput {
  /** Task name key used to look up the config in the registry */
  taskName: string;
  /** Task-specific data passed through to `ContentContext.data` */
  data?: Record<string, unknown> | object;
  /** Policy IDs associated with this task, formatted by `formatPolicies()` */
  affectedPolicies?: string[];
  /** ISO deadline string, formatted to locale date for `ContentContext.formattedDeadline` */
  deadline?: string;
}

// ========== INTERNAL TASK TYPES (Staff-only, see: https://github.com/gobranch/branch/issues/27732) ==========

/**
 * Baseline policy-state snapshot every internal task carries.
 * Captured at bind time so `description()` can render any policy-context phrasing
 * (e.g. "Policy state: OH") without needing to re-fetch the policy. Stable for the
 * life of the task — the policy state and type don't change after bind.
 *
 * Per-task definitions can extend this (`InternalTaskBindStateMap['Some Task']`)
 * if a description needs additional policy-stable fields (effective date, mortgage
 * count, etc.). Anything offer-derived or task-specific belongs in `data`, not here.
 */
export interface BaseInternalTaskBindState {
  state: string;
  policyType: string;
}

// ----- Per-task data shapes -----
// `data` holds the task-specific evidence the trigger captured from the offer/policy
// (addresses, vehicle lists, home characteristics, etc.). Empty object for tasks
// whose description has no per-task variables.

export type IllogicalCombinationReviewData = { flags: string[] };
export type HomeQualityVerificationData = {
  homeQualityFromData: string;
  homeQualityFromOffer: string;
};
export type ConfirmNotAForeclosureData = { foreclosureResponse: Record<string, any> };
export type GarageMailingAddressMismatchData = {
  homeAddress: string;
  mailingAddress: string;
  vehicleAddresses: string[];
};
export type HomeLowMarketValueData = {
  address: string;
  totalReplacementCostEstimate?: number;
  estimatedValueAmount?: number;
  purchaseAmount?: number;
  purchaseDate?: string;
  valuationDate?: string;
};
export type CarsListData = { carsData: string };
export type TrailersListData = { trailersData: string };
export type HomeRequiresManualReviewData = { propertyConcerns: number[] };
export type PrimaryResidenceVerificationData = { homeAddress: string; mailingAddress: string };

export type InspectionPendingData = {
  dateOrdered: string;
  source?: string;
};

/**
 * Maps internal task name -> data type.
 * Parallel to `TaskDataMap` for regular onboarding tasks. Add a new task here when
 * adding it to the `internalTasks` constants.
 */
export interface InternalTaskDataMap {
  'Home Quality Verification': HomeQualityVerificationData;
  'UW Umbrella Review': Record<string, never>;
  'Confirm Not a Foreclosure': ConfirmNotAForeclosureData;
  'Distance to Fire Hydrant': Record<string, never>;
  'Garage/Mailing Address Mismatch': GarageMailingAddressMismatchData;
  'Home Low Market Value': HomeLowMarketValueData;
  'Car With Business Use': CarsListData;
  'Potential Commercial Vehicle': CarsListData;
  'Trailer With Restricted Use': TrailersListData;
  'Illogical Combination Review': IllogicalCombinationReviewData;
  'Home Requires Manual Review': HomeRequiresManualReviewData;
  'Primary Residence Verification': PrimaryResidenceVerificationData;
  'Odd Number of Married Drivers': Record<string, never>;
  'Inspection Pending': InspectionPendingData;
}

/**
 * Maps internal task name -> bindState type.
 * Every task's bindState extends `BaseInternalTaskBindState`, so this map exists
 * to declare per-task extensions. Defaults to the base shape when no extension
 * is needed (which is every current task).
 */
export interface InternalTaskBindStateMap {
  'Home Quality Verification': BaseInternalTaskBindState;
  'UW Umbrella Review': BaseInternalTaskBindState;
  'Confirm Not a Foreclosure': BaseInternalTaskBindState;
  'Distance to Fire Hydrant': BaseInternalTaskBindState;
  'Garage/Mailing Address Mismatch': BaseInternalTaskBindState;
  'Home Low Market Value': BaseInternalTaskBindState;
  'Car With Business Use': BaseInternalTaskBindState;
  'Potential Commercial Vehicle': BaseInternalTaskBindState;
  'Trailer With Restricted Use': BaseInternalTaskBindState;
  'Illogical Combination Review': BaseInternalTaskBindState;
  'Home Requires Manual Review': BaseInternalTaskBindState;
  'Primary Residence Verification': BaseInternalTaskBindState;
  'Odd Number of Married Drivers': BaseInternalTaskBindState;
  'Inspection Pending': BaseInternalTaskBindState;
}

export type InternalTaskDataFor<T extends InternalTaskName> = InternalTaskDataMap[T];
export type InternalTaskBindStateFor<T extends InternalTaskName> = InternalTaskBindStateMap[T];

/**
 * Input passed to every `InternalTaskConfig.trigger()` function.
 * Contains the bound policy and the offer it was bound from. Triggers derive any
 * additional fields they need (Verisk data, addresses, characteristics, etc.) directly
 * off these two records — no pre-computed fields, so all per-task logic lives in the
 * task definition itself.
 */
export interface InternalTaskTriggerInput {
  policy: DynamoDbPolicy;
  /** The bound offer record. Null when offerId is unset or fetch failed. */
  offer: DynamoDbOffer | null;
}

/**
 * Trigger return shape: `{ data, bindState }` when the task should fire, otherwise null.
 * Both fields are typed per task via the data/bindState maps so triggers can't
 * silently drop or misshape required fields.
 */
export interface InternalTaskTriggerOutput<TName extends InternalTaskName> {
  data: InternalTaskDataMap[TName];
  bindState: InternalTaskBindStateMap[TName];
}

/**
 * Context passed to `InternalTaskConfig.description()` when rendering the description
 * on demand. Description never sees the raw policy/offer — everything it needs is in
 * `data` (task-specific) and `bindState` (policy-stable), plus the generic lifecycle
 * fields so multi-phase tasks can return state-appropriate copy without storing it.
 */
export interface InternalTaskContext<TName extends InternalTaskName = InternalTaskName> {
  policyId: string;
  data: InternalTaskDataMap[TName];
  bindState: InternalTaskBindStateMap[TName];
  completedDate?: string;
  approvalDate?: string;
}

/** Context passed to the optional `onComplete.segment` lifecycle hook. */
export interface InternalTaskCompleteContext<TName extends InternalTaskName = InternalTaskName> {
  task: {
    taskId: string;
    taskName: TName | string;
    policyId?: string;
    data?: InternalTaskDataMap[TName] | Record<string, any>;
    bindState?: InternalTaskBindStateMap[TName] | Record<string, any>;
  };
  accountId: string;
  completedBy: string;
}

/** Shape of a Segment event returned by an `onComplete.segment` override. */
export interface InternalTaskCompleteSegmentEvent {
  event: string;
  properties: Record<string, any>;
}

/**
 * Configuration for a single internal task type.
 * Generic over the task name so `trigger()`, `description()`, and the lifecycle hooks
 * are all narrowed to the task's `data` and `bindState` shapes — adding a new field
 * to a task's data type immediately surfaces compile errors at every consumer.
 *
 * Each task owns its own trigger logic and (optionally) lifecycle hooks for
 * auto-approval and completion side-effects, so the lambdas that consume them
 * remain pure plumbing.
 */
export interface InternalTaskConfig<TName extends InternalTaskName = InternalTaskName> {
  /** Human-readable name shown in Staff tools (e.g., "Home quality: review needed") */
  staffDisplayName: string;
  /** Which staff team reviews this task */
  reviewTeam: ReviewTeam;
  /** Policy types this task applies to (e.g., ['H'], ['A'], ['H', 'C']). Used by the
   * framework as an early filter before invoking `trigger()`. */
  policyTypes: string[];
  /** Feature flag for task creation. When false, `trigger()` is never invoked. */
  enabled: boolean;
  /**
   * Marks a task that has a leading "pending" phase before it's ready for review
   * (e.g. an inspection awaiting vendor/AI results). Such a task is `pending` while
   * `completedDate` is unset, then `needsReview` once completed-but-not-approved (or
   * straight to `approved` if it's completed and approved in the same step).
   */
  hasPendingPhase?: boolean;
  /**
   * Returns the task's `{ data, bindState }` payload when this task should be created
   * for the given policy/offer combination, or `null` to skip. All per-task condition
   * logic lives here.
   *
   * NOTE: declared with method syntax (not arrow property) so TS treats the parameter
   * as bivariant — required so a narrowed `InternalTaskDefinition<'Home Quality...'>`
   * can be stored in a uniform `InternalTaskDefinition[]` array used by the dispatcher.
   * Definition sites still get full narrowing at trigger-write time.
   */
  trigger(input: InternalTaskTriggerInput): InternalTaskTriggerOutput<TName> | null;
  /**
   * Renders the task description on demand from the stored `data` + `bindState`.
   * Called by Staff "Learn more" modal at view time, never persisted.
   */
  description(ctx: InternalTaskContext<TName>): string;
  /**
   * Optional per-task overrides for completion side-effects.
   * Each hook returns the side-effect payload; the framework dispatches it.
   */
  onComplete?: {
    /**
     * Returns a custom Segment event to fire when this task is approved by staff.
     * If omitted, the framework fires the default `'Internal task completed'` event.
     */
    segment?(ctx: InternalTaskCompleteContext<TName>): InternalTaskCompleteSegmentEvent;
  };
}

/**
 * Pairs an internal task name with its config.
 * Each file in `carriers/branch/internal-tasks/` exports one of these.
 */
export interface InternalTaskDefinition<TName extends InternalTaskName = InternalTaskName> {
  taskName: TName;
  config: InternalTaskConfig<TName>;
}

/**
 * Helper that lets task files drop the explicit generic annotation.
 *
 * Before:
 *   export const uwUmbrellaReview: InternalTaskDefinition<'UW Umbrella Review'> = { taskName, config };
 *
 * After:
 *   export const uwUmbrellaReview = defineInternalTask({ taskName, config });
 *
 * TypeScript infers `TName` from the `taskName` literal (e.g. `'UW Umbrella Review'`)
 * and narrows `config.trigger` / `config.description` / `config.onComplete.segment`
 * to the matching entries in `InternalTaskDataMap` and `InternalTaskBindStateMap` —
 * catching trigger returns with wrong-shape `data`/`bindState` at the definition site.
 */
export function defineInternalTask<TName extends InternalTaskName>(
  def: InternalTaskDefinition<TName>
): InternalTaskDefinition<TName> {
  return def;
}
